import React, { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { Loader2, Trophy } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

const pctColor = (p) => p >= 70 ? 'text-emerald-600 bg-emerald-50' : p >= 40 ? 'text-amber-600 bg-amber-50' : 'text-rose-600 bg-rose-50';

export default function TeacherAnalyticsPage() {
    const [classes, setClasses] = useState([]);
    const [filter, setFilter] = useState('');
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get('/api/teacher/classrooms').then((r) => setClasses(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        const url = '/api/teacher/analytics' + (filter ? `?classroom_id=${filter}` : '');
        setData(null);
        api.get(url).then((r) => setData(r.data)).catch(() => setData(false));
    }, [filter]);

    return (
        <div className="space-y-6" data-testid="teacher-analytics-page">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Analytics</h1>
                    <p className="text-sm text-zinc-500 mt-1">Class-wide and per-class performance.</p>
                </div>
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-zinc-200 text-sm" data-testid="analytics-classroom-filter">
                    <option value="">All classes</option>
                    {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
            </div>

            {data === null ? (
                <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#7C3AED] animate-spin" /></div>
            ) : data === false ? (
                <div className="text-sm text-zinc-500">Could not load analytics.</div>
            ) : (
                <>
                    {/* Score distribution */}
                    <div className="bg-white rounded-3xl border border-zinc-200 p-6">
                        <h2 className="font-black text-base mb-4">Score distribution</h2>
                        <div style={{ width: '100%', minWidth: 0, height: 220 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.score_distribution} margin={{ top: 10, right: 10, left: -20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#EDE4FF" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                    <Tooltip contentStyle={{ borderRadius: 8, borderColor: '#7C3AED' }} />
                                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                        {data.score_distribution.map((d, i) => {
                                            const colors = ['#F43F5E', '#FB7185', '#F59E0B', '#A78BFA', '#10B981'];
                                            return <Cell key={i} fill={colors[i]} />;
                                        })}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Average by unit */}
                    <div className="bg-white rounded-3xl border border-zinc-200 p-6">
                        <h2 className="font-black text-base mb-4">Average by subject / unit</h2>
                        {data.avg_by_unit.length === 0 ? (
                            <p className="text-sm text-zinc-500 py-6 text-center">No data yet.</p>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {data.avg_by_unit.map((r, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-[#F8F7FC]">
                                        <div>
                                            <div className="text-xs font-bold text-[#7C3AED]">{r.subject}</div>
                                            <div className="text-sm font-bold">{r.unit}</div>
                                            <div className="text-[10px] text-zinc-500">{r.count} submissions</div>
                                        </div>
                                        <span className={`text-xs font-black px-2 py-1 rounded-md ${pctColor(r.avg_pct)}`}>{Math.round(r.avg_pct)}%</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Top missed criteria */}
                    <div className="bg-white rounded-3xl border border-zinc-200 p-6">
                        <h2 className="font-black text-base mb-4">Most missed mark-scheme points</h2>
                        {data.top_missed.length === 0 ? (
                            <p className="text-sm text-zinc-500 py-6 text-center">No data yet.</p>
                        ) : (
                            <ul className="space-y-2">
                                {data.top_missed.map((m, i) => (
                                    <li key={i} className="flex items-center justify-between text-sm">
                                        <span className="flex-1 text-zinc-700">{m.text}</span>
                                        <span className="text-xs font-bold text-rose-600 px-2 py-0.5 rounded-md bg-rose-50">×{m.count}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Leaderboard */}
                    {filter && (
                        <div className="bg-white rounded-3xl border border-zinc-200 p-6">
                            <h2 className="font-black text-base mb-4 inline-flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Leaderboard</h2>
                            {data.leaderboard.length === 0 ? (
                                <p className="text-sm text-zinc-500 py-6 text-center">No submissions yet.</p>
                            ) : (
                                <ol className="space-y-2">
                                    {data.leaderboard.map((row, i) => (
                                        <li key={row.student_id} className="flex items-center gap-3 p-3 rounded-xl bg-[#F8F7FC]" data-testid={`leader-${i}`}>
                                            <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-black ${i < 3 ? 'bg-[#7C3AED] text-white' : 'bg-white text-zinc-500 border border-zinc-200'}`}>{i + 1}</span>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-sm truncate">{row.student_name}</div>
                                                <div className="text-xs text-zinc-500">{row.submission_count} submissions</div>
                                            </div>
                                            <span className={`text-xs font-black px-2 py-1 rounded-md ${pctColor(row.avg_pct)}`}>{Math.round(row.avg_pct)}%</span>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
